import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLazyGetShipmentShareMediaByContainerListQuery,
  useLazyGetShipmentShareMediaByContainerQuery,
  useLazyGetShipmentShareMediaByPhotoQuery,
} from "@/src/store/apis/shipments/shipmentShare";
import {
  useLazyGetShipmentContainersListQuery,
  useLazyGetSingleShipmentContainerMediaQuery,
  useLazyGetShipmentContainerMediaByPhotoTypeQuery,
} from "@/src/store/apis/shipments/shipmentDetail";
import {
  useShipmentShareKeyWithcontainerIdsFullBatchDownloadMutation,
  useShipmentShareKeyWithMediaTypeFullBatchDownloadMutation,
  useContainerIdsFullBatchDownloadMutation,
  useShipmentIdWithMediaTypeFullBatchDownloadMutation,
} from "@/src/store/apis/containerImage";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP } from "@/src/pages/exporter/Shipment/components/dialog/imageMapper";
import {
  ContainerPhotosAdapter,
  ContainerListItem,
  ContainerMediaData,
  MediaByPhotoType,
} from "../types";
import {
  mapContainerMediaResponse,
  mapMediaByPhotoTypeResponse,
} from "./utils/mapContainerMediaResponse";

interface SharedEmailParams {
  shipmentId: number;
  shipmentShareKey?: string;
  isPreview?: boolean;
}

/**
 * Shared Email 모드용 Adapter
 * - 이메일로 공유된 링크를 통해 컨테이너 사진을 조회할 때 사용
 * - shipmentShareKey를 통한 인증 (공개 접근)
 * - isPreview=true일 경우 수출자 API 사용 (미리보기 모드)
 *
 * @param params - 공유 이메일 관련 파라미터
 * @param params.shipmentId - 선적 ID
 * @param params.shipmentShareKey - 공유 키 (공개 접근용)
 * @param params.isPreview - 미리보기 모드 여부 (수출자 API 사용)
 * @returns ContainerPhotosAdapter - 통합 어댑터 인터페이스
 *
 * @example
 * // 공개 공유 링크 모드
 * const adapter = useSharedEmailAdapter({ shipmentId, shipmentShareKey });
 *
 * // 수출자 미리보기 모드
 * const adapter = useSharedEmailAdapter({ shipmentId, isPreview: true });
 */
export const useSharedEmailAdapter = (
  params: SharedEmailParams
): ContainerPhotosAdapter => {
  const { t } = useTranslation();
  const { shipmentId, shipmentShareKey, isPreview = false } = params;

  const [containerList, setContainerList] = useState<ContainerListItem[]>();
  const [containerMedia, setContainerMedia] = useState<ContainerMediaData>();
  const [mediaByPhotoType, setMediaByPhotoType] = useState<MediaByPhotoType>();

  // Shared Email API (공개 접근용)
  const [getSharedContainerList, sharedContainerListResult] =
    useLazyGetShipmentShareMediaByContainerListQuery();
  const [getSharedSingleContainerMedia, sharedSingleContainerMediaResult] =
    useLazyGetShipmentShareMediaByContainerQuery();
  const [getSharedMediaByPhotoType, sharedMediaByPhotoTypeResult] =
    useLazyGetShipmentShareMediaByPhotoQuery();

  // Preview용 (수출자 API - 인증 필요)
  const [getPreviewContainerList, previewContainerListResult] =
    useLazyGetShipmentContainersListQuery();
  const [getPreviewSingleContainerMedia, previewSingleContainerMediaResult] =
    useLazyGetSingleShipmentContainerMediaQuery();
  const [getPreviewMediaByPhotoType, previewMediaByPhotoTypeResult] =
    useLazyGetShipmentContainerMediaByPhotoTypeQuery();

  // Shared Email 다운로드 API
  const [downloadSharedByContainers, sharedContainerDownloadResult] =
    useShipmentShareKeyWithcontainerIdsFullBatchDownloadMutation();
  const [downloadSharedByMediaTypes, sharedPhotoTypeDownloadResult] =
    useShipmentShareKeyWithMediaTypeFullBatchDownloadMutation();

  // Preview용 다운로드 API
  const [downloadPreviewByContainers, previewContainerDownloadResult] =
    useContainerIdsFullBatchDownloadMutation();
  const [downloadPreviewByMediaTypes, previewPhotoTypeDownloadResult] =
    useShipmentIdWithMediaTypeFullBatchDownloadMutation();

  const fetchContainerList = useCallback(async () => {
    if (!shipmentId) return undefined;

    try {
      if (isPreview) {
        // Preview 모드: 수출자 API 사용
        const response = await getPreviewContainerList({ id: shipmentId }).unwrap();
        const mapped: ContainerListItem[] =
          response?.map((item) => ({
            id: item.id,
            containerNo: item.containerNo ?? null,
          })) ?? [];
        setContainerList(mapped);
        return mapped;
      } else {
        // Shared 모드: 공개 API 사용
        if (!shipmentShareKey) return undefined;
        const response = await getSharedContainerList({
          shipmentId,
          shipmentShareKey,
        }).unwrap();
        const mapped: ContainerListItem[] =
          response?.map((item) => ({
            id: item.id,
            containerNo: item.containerNo ?? null,
          })) ?? [];
        setContainerList(mapped);
        return mapped;
      }
    } catch (error) {
      console.error("[useSharedEmailAdapter] fetchContainerList error:", error);
      return undefined;
    }
  }, [shipmentId, shipmentShareKey, isPreview, getSharedContainerList, getPreviewContainerList]);

  const fetchContainerMedia = useCallback(
    async (containerId: number) => {
      try {
        let response;
        if (isPreview) {
          response = await getPreviewSingleContainerMedia({
            id: containerId,
            mode: 'exporter',
            shipmentId,
          }).unwrap();
        } else {
          if (!shipmentShareKey) return;
          response = await getSharedSingleContainerMedia({
            shipmentId,
            shipmentShareKey,
            id: containerId,
          }).unwrap();
        }

        if (response) {
          const mapped = mapContainerMediaResponse(response);
          setContainerMedia(mapped);
        }
      } catch (error) {
        console.error("[useSharedEmailAdapter] fetchContainerMedia error:", error);
      }
    },
    [shipmentId, shipmentShareKey, isPreview, getSharedSingleContainerMedia, getPreviewSingleContainerMedia]
  );

  const fetchMediaByPhotoType = useCallback(async () => {
    if (!shipmentId) return;

    try {
      let response;
      if (isPreview) {
        response = await getPreviewMediaByPhotoType({ id: shipmentId }).unwrap();
      } else {
        if (!shipmentShareKey) return;
        response = await getSharedMediaByPhotoType({
          shipmentId,
          shipmentShareKey,
        }).unwrap();
      }

      if (response) {
        const mapped = mapMediaByPhotoTypeResponse(response);
        setMediaByPhotoType(mapped);
      }
    } catch (error) {
      console.error("[useSharedEmailAdapter] fetchMediaByPhotoType error:", error);
    }
  }, [shipmentId, shipmentShareKey, isPreview, getSharedMediaByPhotoType, getPreviewMediaByPhotoType]);

  const downloadByContainerIds = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;

      try {
        if (isPreview) {
          await downloadPreviewByContainers({ containerIdList: ids }).unwrap();
        } else {
          if (!shipmentShareKey) return;
          await downloadSharedByContainers({
            shipmentId,
            shipmentShareKey,
            containerIdList: ids,
          }).unwrap();
        }
      } catch (error) {
        console.error("[useSharedEmailAdapter] downloadByContainerIds error:", error);
      }
    },
    [shipmentId, shipmentShareKey, isPreview, downloadSharedByContainers, downloadPreviewByContainers]
  );

  const downloadByPhotoTypes = useCallback(
    async (types: ContainerMediaType[]) => {
      if (types.length === 0 || !shipmentId) return;

      const normalizedTypes = types
        .map((type) => (type.startsWith("CUSTOM_") ? "CUSTOM" : type))
        .filter((type, index, self) => self.indexOf(type) === index) as ContainerMediaType[];

      try {
        if (isPreview) {
          await downloadPreviewByMediaTypes({
            id: shipmentId,
            containerMediaTypeList: normalizedTypes,
          }).unwrap();
        } else {
          if (!shipmentShareKey) return;
          await downloadSharedByMediaTypes({
            shipmentId,
            shipmentShareKey,
            containerMediaTypeList: normalizedTypes,
          }).unwrap();
        }
      } catch (error) {
        console.error("[useSharedEmailAdapter] downloadByPhotoTypes error:", error);
      }
    },
    [shipmentId, shipmentShareKey, isPreview, downloadSharedByMediaTypes, downloadPreviewByMediaTypes]
  );

  const getPhotoTypeLabel = useCallback(
    (type: ContainerMediaType): string => {
      const labelMap = CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP(t);
      return labelMap[type] || type;
    },
    [t]
  );

  // Loading states - Preview와 Shared 중 적절한 것 선택
  const isContainerListLoading = isPreview
    ? previewContainerListResult.isFetching
    : sharedContainerListResult.isFetching;

  const isContainerMediaLoading = isPreview
    ? previewSingleContainerMediaResult.isFetching
    : sharedSingleContainerMediaResult.isFetching;

  const isMediaByPhotoTypeLoading = isPreview
    ? previewMediaByPhotoTypeResult.isFetching
    : sharedMediaByPhotoTypeResult.isFetching;

  const isContainerDownloading = isPreview
    ? previewContainerDownloadResult.isLoading
    : sharedContainerDownloadResult.isLoading;

  const isPhotoTypeDownloading = isPreview
    ? previewPhotoTypeDownloadResult.isLoading
    : sharedPhotoTypeDownloadResult.isLoading;

  return {
    containerList,
    containerMedia,
    mediaByPhotoType,

    isContainerListLoading,
    isContainerMediaLoading,
    isMediaByPhotoTypeLoading,

    fetchContainerList,
    fetchContainerMedia,
    fetchMediaByPhotoType,

    downloadByContainerIds,
    downloadByPhotoTypes,
    isContainerDownloading,
    isPhotoTypeDownloading,

    getPhotoTypeLabel,
  };
};
